import { Box, Button, Heading, HStack, Text, VStack } from "components/ui";
import {
  ServerAuthorizationConfig,
  AuthorizationType,
} from "components/common/ServerAuthorizationConfig";
import { generateSecret } from "components/common/TokenEndpointConfig";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { serverService } from "services/serverService";
import { widgetService } from "services/widgetService";
import { getThemeColor } from "theme/colors";

interface Step2AuthorizationProps {
  onNext: (data: {
    serverId: string;
    widgetId: string;
    authConfig: any;
  }) => void;
  onBack: () => void;
  serverData: {
    serverId: string;
    serverName: string;
    serverDescription: string;
    docUrl: string;
    docType: number;
  };
  initialData?: {
    authConfig?: any;
  };
}

export const Step2Authorization = ({
  onNext,
  onBack,
  serverData,
  initialData,
}: Step2AuthorizationProps) => {
  const { t } = useTranslation();
  const [authorizationType, setAuthorizationType] = useState<AuthorizationType>(
    (initialData?.authConfig?.authorizationType as AuthorizationType) || "None",
  );
  const [staticAuthHeaders, setStaticAuthHeaders] = useState(
    initialData?.authConfig?.staticAuthHeaders || "",
  );
  const [dynamicAuthHeaders, setDynamicAuthHeaders] = useState(
    initialData?.authConfig?.dynamicAuthHeaders || "",
  );
  const [allowAuthUpdate, setAllowAuthUpdate] = useState(
    initialData?.authConfig?.allowAuthUpdate ?? true,
  );
  const [authInstructionsUrl, setAuthInstructionsUrl] = useState(
    initialData?.authConfig?.authInstructionsUrl || "",
  );
  // CustomAuthEndpoint state
  const [autoTokenEndpoint, setAutoTokenEndpoint] = useState(
    initialData?.authConfig?.autoTokenEndpoint || "",
  );
  const [autoTokenMethod, setAutoTokenMethod] = useState(
    initialData?.authConfig?.autoTokenMethod || "POST",
  );
  const [autoTokenRequestHeaders, setAutoTokenRequestHeaders] = useState(
    initialData?.authConfig?.autoTokenRequestHeaders || "",
  );
  const [autoTokenRequestBody, setAutoTokenRequestBody] = useState(
    initialData?.authConfig?.autoTokenRequestBody || "",
  );
  const [autoTokenRequestPlaceholders, setAutoTokenRequestPlaceholders] =
    useState(initialData?.authConfig?.autoTokenRequestPlaceholders || "");
  const [autoTokenResponseExample, setAutoTokenResponseExample] = useState(
    initialData?.authConfig?.autoTokenResponseExample || "",
  );
  const [autoTokenResponsePath, setAutoTokenResponsePath] = useState(
    initialData?.authConfig?.autoTokenResponsePath || "",
  );
  const [autoTokenHeaderName, setAutoTokenHeaderName] = useState(
    initialData?.authConfig?.autoTokenHeaderName || "Authorization",
  );
  const [autoTokenHeaderFormat, setAutoTokenHeaderFormat] = useState(
    initialData?.authConfig?.autoTokenHeaderFormat || "Bearer {token}",
  );
  const [autoTokenSecret, setAutoTokenSecret] = useState(
    initialData?.authConfig?.autoTokenSecret || "",
  );
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    autoTokenEndpoint?: string;
    autoTokenResponsePath?: string;
    autoTokenHeaderName?: string;
    autoTokenHeaderFormat?: string;
  }>({});

  const handleAuthConfigChange = (field: string, value: any) => {
    // Clear validation error for this field
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    switch (field) {
      case "authorizationType":
        setAuthorizationType(value);
        // Auto-generate secret when switching to CustomAuthEndpoint
        if (value === "CustomAuthEndpoint" && !autoTokenSecret) {
          setAutoTokenSecret(generateSecret());
        }
        break;
      case "staticAuthHeaders":
        setStaticAuthHeaders(value);
        break;
      case "dynamicAuthHeaders":
        setDynamicAuthHeaders(value);
        break;
      case "allowAuthUpdate":
        setAllowAuthUpdate(value);
        break;
      case "authInstructionsUrl":
        setAuthInstructionsUrl(value);
        break;
      case "autoTokenEndpoint":
        setAutoTokenEndpoint(value);
        break;
      case "autoTokenMethod":
        setAutoTokenMethod(value);
        break;
      case "autoTokenRequestHeaders":
        setAutoTokenRequestHeaders(value);
        break;
      case "autoTokenRequestBody":
        setAutoTokenRequestBody(value);
        break;
      case "autoTokenRequestPlaceholders":
        setAutoTokenRequestPlaceholders(value);
        break;
      case "autoTokenResponseExample":
        setAutoTokenResponseExample(value);
        break;
      case "autoTokenResponsePath":
        setAutoTokenResponsePath(value);
        break;
      case "autoTokenHeaderName":
        setAutoTokenHeaderName(value);
        break;
      case "autoTokenHeaderFormat":
        setAutoTokenHeaderFormat(value);
        break;
      case "autoTokenSecret":
        setAutoTokenSecret(value);
        break;
    }
  };

  const handleNext = async () => {
    // Validate CustomAuthEndpoint configuration
    if (authorizationType === "CustomAuthEndpoint") {
      const errors: typeof validationErrors = {};

      if (!autoTokenEndpoint || autoTokenEndpoint.trim() === "") {
        errors.autoTokenEndpoint = t(
          "app.servers.authorization.tokenEndpoint.urlRequired",
        );
      }
      if (!autoTokenResponsePath || autoTokenResponsePath.trim() === "") {
        errors.autoTokenResponsePath = t(
          "app.servers.authorization.tokenEndpoint.responsePathRequired",
        );
      }
      if (!autoTokenHeaderName || autoTokenHeaderName.trim() === "") {
        errors.autoTokenHeaderName = t(
          "app.servers.authorization.tokenEndpoint.headerNameRequired",
        );
      }
      if (!autoTokenHeaderFormat || autoTokenHeaderFormat.trim() === "") {
        errors.autoTokenHeaderFormat = t(
          "app.servers.authorization.tokenEndpoint.headerFormatRequired",
        );
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
    }

    setValidationErrors({});

    setIsCreating(true);

    try {
      // Update server with authorization configuration
      await serverService.updateServer(serverData.serverId, {
        authorizationType,
        staticAuthHeaders: staticAuthHeaders || undefined,
        dynamicAuthHeaders: dynamicAuthHeaders || undefined,
        allowAuthUpdate,
        authInstructionsUrl: authInstructionsUrl || undefined,
        // CustomAuthEndpoint fields
        autoTokenEndpoint: autoTokenEndpoint || undefined,
        autoTokenMethod: autoTokenMethod || undefined,
        autoTokenRequestHeaders: autoTokenRequestHeaders || undefined,
        autoTokenRequestBody: autoTokenRequestBody || undefined,
        autoTokenRequestPlaceholders: autoTokenRequestPlaceholders || undefined,
        autoTokenResponseExample: autoTokenResponseExample || undefined,
        autoTokenResponsePath: autoTokenResponsePath || undefined,
        autoTokenHeaderName: autoTokenHeaderName || undefined,
        autoTokenHeaderFormat: autoTokenHeaderFormat || undefined,
        autoTokenSecret: autoTokenSecret || undefined,
      });

      // Create widget with serverIds array
      const widgetResponse = await widgetService.createWidget({
        name: `${serverData.serverName} Copilot`,
        serverIds: [serverData.serverId],
      });

      toaster.create({
        title: t("app.onboarding.step2.success"),
        description: t("app.onboarding.step2.serverConfigured"),
        type: "success",
      });

      onNext({
        serverId: serverData.serverId,
        widgetId: widgetResponse.id,
        authConfig: {
          authorizationType,
          staticAuthHeaders,
          dynamicAuthHeaders,
          allowAuthUpdate,
          authInstructionsUrl,
        },
      });
    } catch (error: any) {
      toaster.create({
        title: t("app.onboarding.step2.failed"),
        description: error.message || "An error occurred",
        type: "error",
      });
      setIsCreating(false);
    }
  };

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={4}>
        <VStack gap={4} align="stretch">
          <Box>
            <Heading size="lg" mb={2} color={getThemeColor("heading")}>
              {t("app.onboarding.step2.title")}
            </Heading>
            <Text fontSize="sm" color={getThemeColor("text")}>
              {t("app.onboarding.step2.description")}
            </Text>
          </Box>

          <ServerAuthorizationConfig
            authorizationType={authorizationType}
            staticAuthHeaders={staticAuthHeaders}
            dynamicAuthHeaders={dynamicAuthHeaders}
            allowAuthUpdate={allowAuthUpdate}
            authInstructionsUrl={authInstructionsUrl}
            autoTokenEndpoint={autoTokenEndpoint}
            autoTokenMethod={autoTokenMethod}
            autoTokenRequestHeaders={autoTokenRequestHeaders}
            autoTokenRequestBody={autoTokenRequestBody}
            autoTokenRequestPlaceholders={autoTokenRequestPlaceholders}
            autoTokenResponseExample={autoTokenResponseExample}
            autoTokenResponsePath={autoTokenResponsePath}
            autoTokenHeaderName={autoTokenHeaderName}
            autoTokenHeaderFormat={autoTokenHeaderFormat}
            autoTokenSecret={autoTokenSecret}
            onChange={handleAuthConfigChange}
            errors={validationErrors}
          />
        </VStack>
      </Box>

      {/* Fixed Footer */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        bg={getThemeColor("modal.bg")}
        py={4}
        px={6}
        boxShadow="sm"
      >
        <HStack justify="space-between">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isCreating}
            size="md"
          >
            {t("app.common.back")}
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleNext}
            loading={isCreating}
            disabled={isCreating}
            size="md"
          >
            {t("app.common.next")}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};
